import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Eye, Code as CodeIcon } from 'lucide-react';

/**
 * Lightweight side-by-side Markdown editor used for the offer-letter
 * Roles & Responsibilities and Terms & Conditions fields.
 *
 * Pure presentational component — state lives in the parent.
 * Avoids recursive React (per project constraints).
 */
export default function MarkdownEditor({
  value, onChange,
  rows = 10,
  placeholder = 'Write in Markdown… use `## Headings`, `**bold**`, `- bullets`',
  testid = 'markdown-editor',
  className = '',
}) {
  const [tab, setTab] = useState('write'); // 'write' | 'preview'
  return (
    <div data-testid={testid} className={`rounded-xl border border-slate-200 bg-white overflow-hidden ${className}`}>
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-1.5">
        <div className="flex gap-1 text-xs font-semibold">
          <button
            type="button"
            data-testid={`${testid}-tab-write`}
            onClick={() => setTab('write')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors ${tab === 'write' ? 'bg-white text-[#3D4A8E] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <CodeIcon className="w-3.5 h-3.5" /> Write
          </button>
          <button
            type="button"
            data-testid={`${testid}-tab-preview`}
            onClick={() => setTab('preview')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors ${tab === 'preview' ? 'bg-white text-[#3D4A8E] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Markdown</div>
      </div>

      {tab === 'write' ? (
        <textarea
          data-testid={`${testid}-textarea`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="w-full px-3 py-2 text-sm font-mono text-slate-800 leading-relaxed focus:outline-none resize-y"
        />
      ) : (
        <div
          data-testid={`${testid}-preview`}
          className="prose prose-sm max-w-none px-4 py-3 text-slate-800 min-h-[160px]">
          {(value || '').trim()
            ? <MarkdownView source={value} />
            : <div className="text-slate-400 italic">Nothing to preview yet.</div>}
        </div>
      )}
    </div>
  );
}

export function MarkdownView({ source }) {
  // No recursive components — react-markdown is a single render call.
  return (
    <ReactMarkdown
      components={{
        h1: ({ node, ...p }) => <h3 className="text-base font-bold text-slate-900 mt-3 mb-1.5" {...p} />,
        h2: ({ node, ...p }) => <h4 className="text-sm font-bold text-slate-900 mt-3 mb-1.5" {...p} />,
        h3: ({ node, ...p }) => <h5 className="text-sm font-semibold text-slate-800 mt-2.5 mb-1" {...p} />,
        p:  ({ node, ...p }) => <p className="text-sm text-slate-700 leading-relaxed my-1.5" {...p} />,
        ul: ({ node, ...p }) => <ul className="list-disc list-outside ml-5 text-sm text-slate-700 space-y-1 my-1.5" {...p} />,
        ol: ({ node, ...p }) => <ol className="list-decimal list-outside ml-5 text-sm text-slate-700 space-y-1 my-1.5" {...p} />,
        li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
        strong: ({ node, ...p }) => <strong className="font-semibold text-slate-900" {...p} />,
        em: ({ node, ...p }) => <em className="italic" {...p} />,
        a:  ({ node, ...p }) => <a className="text-[#3D4A8E] underline" target="_blank" rel="noreferrer" {...p} />,
      }}
    >
      {source || ''}
    </ReactMarkdown>
  );
}
